/**
 * Action Result Bridge
 *
 * Unified result surface used by core actions, command palette actions,
 * and plugin-triggered actions. This keeps success/error/info reporting
 * consistent across all UX surfaces.
 */

export type ActionResultLevel = 'success' | 'error' | 'warning' | 'info';

export interface ActionResultPayload {
  level: ActionResultLevel;
  title: string;
  message?: string;
  source?: string;
}

type ActionResultHandler = (payload: ActionResultPayload) => void;

let _handler: ActionResultHandler = (payload) => {
  const line = payload.message ? `${payload.title}: ${payload.message}` : payload.title;
  if (payload.level === 'error') {
    console.error('[ActionResult]', line);
  } else if (payload.level === 'warning') {
    console.warn('[ActionResult]', line);
  } else {
    console.log('[ActionResult]', line);
  }
};

export const actionResultBridge = {
  connect(handler: ActionResultHandler): void {
    _handler = handler;
  },

  disconnect(): void {
    _handler = (payload) => {
      const line = payload.message ? `${payload.title}: ${payload.message}` : payload.title;
      if (payload.level === 'error') console.error('[ActionResult]', line);
      else if (payload.level === 'warning') console.warn('[ActionResult]', line);
      else console.log('[ActionResult]', line);
    };
  },

  report(payload: ActionResultPayload): void {
    _handler(payload);
  },

  success(title: string, message?: string, source?: string): void {
    _handler({ level: 'success', title, message, source });
  },

  error(title: string, message?: string, source?: string): void {
    _handler({ level: 'error', title, message, source });
  },

  warning(title: string, message?: string, source?: string): void {
    _handler({ level: 'warning', title, message, source });
  },

  info(title: string, message?: string, source?: string): void {
    _handler({ level: 'info', title, message, source });
  },
};
